import { collection, doc, getDoc, setDoc } from 'firebase/firestore'
import { fs } from '../../../../../../services/firebase/firebaseConfig'

export const TMS_LINK_REFS_COLLECTION = 'TMS_LINK_REFS'

export type StopType = 'Pickup' | 'Delivery'

/** A single-key object, e.g. { Pickup: "10978***docId" }. */
export type LogisticsEntry = Record<string, string>

export interface ITmsLinkRef {
    CreationDate: string
    LastUpdated: string
    Logistics: LogisticsEntry[]
    User: string
}

export interface LinkStop {
    orderType: StopType
    orderId: string
    invoice?: string
}

export interface ResolvedStopRef {
    orderType: string
    orderId: string
    invoice: string
}

/** Build the ordered Logistics array from stops (array order = route sequence). */
export function buildLogisticsEntries(stops: LinkStop[]): LogisticsEntry[] {
    return stops.map((s) => ({ [s.orderType]: `${s.invoice ?? ''}***${s.orderId}` }))
}

/** Parse an "invoice***id" value. Returns null when no usable orderId is present. */
export function parseLinkValue(value: string): { invoice: string; orderId: string } | null {
    if (typeof value !== 'string' || !value) return null
    const parts = value.split('***')
    if (parts.length === 2) {
        if (!parts[1]) return null
        return { invoice: parts[0], orderId: parts[1] }
    }
    // No delimiter: treat the whole thing as a bare id (legacy tolerance).
    return parts[0] ? { invoice: '', orderId: parts[0] } : null
}

/** Flatten a ref doc's Logistics array into ordered, parsed stop refs. */
export function extractStopRefs(ref: ITmsLinkRef | null | undefined): ResolvedStopRef[] {
    if (!ref?.Logistics?.length) return []
    const out: ResolvedStopRef[] = []
    for (const entry of ref.Logistics) {
        const orderType = Object.keys(entry || {})[0]
        if (!orderType) continue
        const parsed = parseLinkValue(entry[orderType])
        if (!parsed) continue
        out.push({ orderType, orderId: parsed.orderId, invoice: parsed.invoice })
    }
    return out
}

/** First pickup + LAST delivery (route order) from parsed, ordered stops. */
export function pickFirstPickupLastDelivery(stops: ResolvedStopRef[]): {
    firstPickupId: string | null
    lastDeliveryId: string | null
} {
    const firstPickup = stops.find((s) => s.orderType === 'Pickup')
    let lastDelivery: ResolvedStopRef | undefined
    for (const s of stops) if (s.orderType === 'Delivery') lastDelivery = s
    return { firstPickupId: firstPickup?.orderId ?? null, lastDeliveryId: lastDelivery?.orderId ?? null }
}

export interface ResolvedLinkedGroup {
    stops: ResolvedStopRef[]
    firstPickupId: string | null
    lastDeliveryId: string | null
}

/**
 * Batch-resolve TMS_LINK_REFS docs into a { refId -> { stops, firstPickupId,
 * lastDeliveryId } } map. Non-string / blank refs are ignored; missing or failed
 * fetches are skipped so callers degrade gracefully. Refs are deduped first.
 */
export async function resolveLinkedGroups(
    refIds: Array<string | null | undefined>,
): Promise<Record<string, ResolvedLinkedGroup>> {
    const unique = Array.from(
        new Set((refIds || []).filter((r): r is string => typeof r === 'string' && !!r)),
    )
    const out: Record<string, ResolvedLinkedGroup> = {}
    await Promise.all(
        unique.map(async (refId) => {
            try {
                const snap = await getDoc(doc(collection(fs, TMS_LINK_REFS_COLLECTION), refId))
                if (!snap.exists()) return
                const stops = extractStopRefs(snap.data() as ITmsLinkRef)
                out[refId] = { stops, ...pickFirstPickupLastDelivery(stops) }
            } catch {
                /* skip — caller degrades gracefully */
            }
        }),
    )
    return out
}

/**
 * Split a resolved group map's members into the ones already fetched and the ones
 * still to look up.
 *
 * `pool` is the caller's order cache keyed by id (the grid's fetched rows, live and
 * archived). Members absent from it are returned as `missingIds`, in first-seen
 * order, for a single by-id fetch — typically the legs of a group that fall outside
 * the fetched window, or that have already been archived out of Firestore.
 */
export function partitionGroupMemberFields(
    groups: Record<string, ResolvedLinkedGroup> | null | undefined,
    pool: Record<string, any> | null | undefined,
): { fieldsById: Record<string, any>; missingIds: string[] } {
    const fieldsById: Record<string, any> = {}
    const missingIds: string[] = []
    const seen = new Set<string>()

    for (const group of Object.values(groups || {})) {
        for (const stop of group?.stops || []) {
            const id = stop?.orderId
            if (!id || seen.has(id)) continue
            seen.add(id)
            const fromPool = pool?.[id]
            if (fromPool) fieldsById[id] = fromPool
            else missingIds.push(id)
        }
    }

    return { fieldsById, missingIds }
}

/** Assemble a full TMS_LINK_REFS document. `now` must be an ISO-8601 string. */
export function buildLinkRefPayload(stops: LinkStop[], userEmail: string, now: string): ITmsLinkRef {
    return {
        CreationDate: now,
        LastUpdated: now,
        Logistics: buildLogisticsEntries(stops),
        User: userEmail,
    }
}

// ---- Firestore wrappers (thin; pure logic above is unit-tested) ----

/** Mint a new ref-doc id without writing it (for use inside an existing batch). */
export function newLinkRefId(): string {
    return doc(collection(fs, TMS_LINK_REFS_COLLECTION)).id
}

/**
 * Resolve the linked stops for an order. Returns [] unless LinkedOrders is a
 * string ref (hard cutover). Optionally excludes the order's own id.
 */
export async function getLinkRefStops(
    linkedOrders: unknown,
    opts?: { excludeOrderId?: string },
): Promise<ResolvedStopRef[]> {
    if (typeof linkedOrders !== 'string' || !linkedOrders) return []
    let snap
    try {
        snap = await getDoc(doc(collection(fs, TMS_LINK_REFS_COLLECTION), linkedOrders))
    } catch {
        // A denied read (the external shared view is unauthenticated, and the rules
        // require request.auth) degrades to "no linked stops", the same as a missing
        // ref doc — matching resolveLinkedGroups. Callers render best-effort; an
        // escaping rejection previously left the stop list stuck loading.
        return []
    }
    if (!snap.exists()) return []
    const stops = extractStopRefs(snap.data() as ITmsLinkRef)
    return opts?.excludeOrderId ? stops.filter((s) => s.orderId !== opts.excludeOrderId) : stops
}

/** Create a ref doc immediately (non-batch callers). Returns the new id. */
export async function createLinkRef(stops: LinkStop[], userEmail: string): Promise<string> {
    const id = newLinkRefId()
    await setDoc(
        doc(collection(fs, TMS_LINK_REFS_COLLECTION), id),
        buildLinkRefPayload(stops, userEmail, new Date().toISOString()),
    )
    return id
}
